import {
  ClienteUnidadNegocio,
  DetalleZona,
  Especialidad,
  EmpleadoCategoriaClienteAsociada,
  EmpleadoEspecialidadAsociada,
  EmpleadoServicioAsociado,
  EmpleadoUnidadNegocioAsociada,
  EmpleadoZonaAsociada,
  Servicio
} from 'src/app/domain/model/asociacion-empleado/asociacion-empleado';
import { CategoriaCliente } from '@model/categoria-cliente/categoria-cliente';
import { CategoriaClienteRepository } from 'src/app/domain/service/categoria-cliente/categoria-cliente-repository';
import { HttpCatalogoSeleccionService } from 'src/app/infrastructure/adapters/catalogo-seleccion/http-catalogo-seleccion.service';
import { AsociacionCatalogoConfig } from './asociacion-catalogo.config';

/**
 * Fábrica de los 5 configs de asociación catálogo↔empleado (HU1259149,
 * Fase 3). Cada función construye el {@link AsociacionCatalogoConfig} de un
 * catálogo — ver Fase 0/1 de HU1259149.md para la tabla de claves de
 * duplicado, mensajes exactos y qué catálogos tienen "modificar".
 */

export function configServicio(catalogo: HttpCatalogoSeleccionService): AsociacionCatalogoConfig<EmpleadoServicioAsociado, Servicio> {
  return {
    id: 'servicio',
    tituloLista: 'Servicios',
    // 3 columnas, igual que ListarServiciosEmpViewImpl (hallazgo C-1 + M-1).
    columnasLista: [
      { campo: 'dsnombreServicio', header: 'Nombre Servicio' },
      { campo: 'nmcalificacion', header: 'Calificación' },
      { campo: 'fealta', header: 'Fecha Inicio' }
    ],
    recursoAsociacion: 'servicios',
    permiteModificar: true,
    permiteAltaIndividual: false,
    permiteBajaMultiple: false,
    validaDuplicado: true,
    // El diálogo del legado muestra Servicio + Unidad de Medida (M-4) y no
    // tiene filtro de texto (M-3): solo lista + checkbox.
    columnasCatalogo: [
      { campo: 'dsnombreServicio', header: 'Servicio' },
      { campo: 'dstipoUnidad', header: 'Unidad de Medida' }
    ],
    filtrosCatalogo: [],
    requiereCliente: false,
    buscarCatalogo: (filtros, _cliente, size, cursor, direction) =>
      catalogo.buscar<Servicio>('servicios', { nombre: filtros['nombre'] }, size, cursor, direction),
    idItemCatalogo: (item) => item.nmconsecutivo,
    // fealta = "Fecha Inicio" obligatoria del diálogo de agregar (hallazgo C-1).
    construirBodyAsociar: (seleccionados, _cliente, extra) => ({
      nmconsecutivosServicio: seleccionados.map((s) => s.nmconsecutivo),
      nmcalificacion: extra?.['nmcalificacion'] ?? null,
      fealta: extra?.['fealta'] ?? null
    }),
    construirBodyModificar: (_fila, cambios) => ({
      nmcalificacion: cambios['nmcalificacion'],
      fealta: cambios['fealta']
    }),
    camposEditables: [
      { campo: 'nmcalificacion', label: 'Calificación', tipo: 'numero', min: 0, max: 99 },
      { campo: 'fealta', label: 'Fecha Inicio', tipo: 'fecha' }
    ],
    // Calificación también es obligatoria al agregar en el legado — es el
    // único campo cuya obligatoriedad SÍ bloquea el guardado en
    // AsignarMultiplesServiciosEmpViewImpl.validateRequiredFields()
    // (la de Fecha Inicio queda comentada ahí, aunque el campo del formulario
    // y la columna de la grilla también la marcan como obligatoria) —
    // hallazgo C-5. Rango 0-99: tbCalificacion.setMaxLength(2).
    camposAltaLote: [
      { campo: 'nmcalificacion', label: 'Calificación', tipo: 'numero', requerido: true, min: 0, max: 99 },
      { campo: 'fealta', label: 'Fecha Inicio', tipo: 'fecha', requerido: true }
    ]
  };
}

export function configEspecialidad(catalogo: HttpCatalogoSeleccionService): AsociacionCatalogoConfig<EmpleadoEspecialidadAsociada, Especialidad> {
  return {
    id: 'especialidad',
    tituloLista: 'Especialidades',
    // 2 columnas, igual que ListarEspecialidadesEmpViewImpl (M-2).
    columnasLista: [
      { campo: 'dsespecialidad', header: 'Especialidades' },
      { campo: 'fealta', header: 'Fecha Inicio' }
    ],
    recursoAsociacion: 'especialidades',
    permiteModificar: false,
    permiteAltaIndividual: false,
    permiteBajaMultiple: true,
    validaDuplicado: true,
    // El diálogo del legado es solo lista + checkbox, sin filtro (M-3).
    columnasCatalogo: [{ campo: 'dsespecialidad', header: 'Especialidad' }],
    filtrosCatalogo: [],
    requiereCliente: false,
    buscarCatalogo: (filtros, _cliente, size, cursor, direction) =>
      catalogo.buscar<Especialidad>('especialidades', { nombre: filtros['nombre'] }, size, cursor, direction),
    idItemCatalogo: (item) => item.cdespecialidad,
    construirBodyAsociar: (seleccionados) => ({
      cdespecialidades: seleccionados.map((e) => e.cdespecialidad)
    })
  };
}

export function configUnidadNegocio(catalogo: HttpCatalogoSeleccionService): AsociacionCatalogoConfig<EmpleadoUnidadNegocioAsociada, ClienteUnidadNegocio> {
  return {
    id: 'unidadNegocio',
    tituloLista: 'Unidades de Negocio',
    columnasLista: [
      { campo: 'dsunidadAdministrativa', header: 'Unidad de Negocio' },
      // El legado rotula esta columna "Aseguradora", no "Cliente" (M-1), y
      // muestra además "Fecha Inicio" de la unidad administrativa (M-2).
      { campo: 'dsnombreComercialCliente', header: 'Aseguradora' },
      { campo: 'feinicioUnidad', header: 'Fecha Inicio' }
    ],
    recursoAsociacion: 'unidades-negocio',
    permiteModificar: false,
    permiteAltaIndividual: false,
    // El legado da de baja en lote: `saEliminar` + `getSelectedRowsObject()`,
    // y el handler `eliminarUnidadesNegocioEmpl` recorre una lista (C-2).
    permiteBajaMultiple: true,
    validaDuplicado: true,
    // El diálogo del legado muestra además Fecha Inicio/Fecha Fin de la
    // unidad administrativa (solo lectura) — M-4.
    columnasCatalogo: [
      { campo: 'dsunidadAdministrativa', header: 'Unidad de Negocio' },
      { campo: 'feinicioUnidad', header: 'Fecha Inicio' },
      { campo: 'fefinUnidad', header: 'Fecha Fin' }
    ],
    filtrosCatalogo: [],
    requiereCliente: true,
    buscarCatalogo: (_filtros, cliente, size, cursor, direction) =>
      catalogo.buscar<ClienteUnidadNegocio>('clientes-unidades-negocio', { nmconsecutivoCliente: cliente ?? undefined }, size, cursor, direction),
    idItemCatalogo: (item) => item.nmconsecutivo,
    construirBodyAsociar: (seleccionados, cliente, extra) => ({
      nmconsecClteUndAdtiva: seleccionados.map((u) => u.nmconsecutivo),
      dsnombreComercialCliente: extra?.['dsnombreComercialCliente'] ?? ''
    })
  };
}

export function configCategoriaCliente(
  categoriaClienteRepository: CategoriaClienteRepository
): AsociacionCatalogoConfig<EmpleadoCategoriaClienteAsociada, CategoriaCliente> {
  return {
    id: 'categoriaCliente',
    tituloLista: 'Categorías Cliente',
    // 3 columnas, igual que ListarCategoriasEmpViewImpl: "Detalle" es la que
    // identifica el registro concreto (C-3); la etiqueta del cliente es
    // "Aseguradora" (M-1).
    columnasLista: [
      { campo: 'dsnombreCategoria', header: 'Categoría' },
      { campo: 'dsnombreDetalleCat', header: 'Detalle' },
      { campo: 'dsnombreComercialCliente', header: 'Aseguradora' }
    ],
    recursoAsociacion: 'categorias-cliente',
    permiteModificar: false,
    permiteAltaIndividual: false,
    permiteBajaMultiple: false,
    validaDuplicado: true,
    // El diálogo del legado muestra Categoría + Detalle (M-4).
    columnasCatalogo: [
      { campo: 'dsnombreCategoria', header: 'Categoría' },
      { campo: 'dsnombreDetalleCat', header: 'Detalle' }
    ],
    filtrosCatalogo: [],
    requiereCliente: true,
    // CategoriaClienteRepository retorna lista plana (sin paginación cursor,
    // Fase 2.5) — se adapta a AsociacionPage con hasNext/hasPrevious false.
    buscarCatalogo: async (_filtros, cliente) => {
      const content = cliente ? await categoriaClienteRepository.obtenerCategoriasCliente(cliente) : [];
      return { content, nextCursor: null, previousCursor: null, hasNext: false, hasPrevious: false };
    },
    idItemCatalogo: (item) => item.nmconsecutivo,
    construirBodyAsociar: (seleccionados, _cliente, extra) => ({
      nmconsecutivosDetCatClt: seleccionados.map((c) => c.nmconsecutivo),
      dsnombreComercialCliente: extra?.['dsnombreComercialCliente'] ?? ''
    })
  };
}

export function configZona(catalogo: HttpCatalogoSeleccionService): AsociacionCatalogoConfig<EmpleadoZonaAsociada, DetalleZona> {
  return {
    id: 'zona',
    tituloLista: 'Zonas',
    // 5 columnas, igual que ListarZonasAsociadasEmpViewImpl (hallazgo C-4).
    columnasLista: [
      { campo: 'dsdepartamento', header: 'Departamento' },
      { campo: 'dszona', header: 'Zona' },
      { campo: 'dsmunicipio', header: 'Municipio' },
      { campo: 'dslocalidad', header: 'Localidad' },
      { campo: 'dsbarrio', header: 'Barrio' }
    ],
    recursoAsociacion: 'zonas',
    permiteModificar: true,
    permiteAltaIndividual: true,
    permiteBajaMultiple: false,
    validaDuplicado: false,
    columnasCatalogo: [
      { campo: 'dsdepartamento', header: 'Departamento' },
      { campo: 'dslocalidad', header: 'Localidad' },
      { campo: 'dsbarrio', header: 'Barrio' }
    ],
    filtrosCatalogo: [
      { campo: 'cddepartamento', label: 'Departamento (código)', placeholder: 'Ej: 05' },
      { campo: 'texto', label: 'Localidad / Barrio', placeholder: 'Buscar...' }
    ],
    requiereCliente: false,
    buscarCatalogo: (filtros, _cliente, size, cursor, direction) =>
      catalogo.buscar<DetalleZona>('detalles-zona', { cddepartamento: filtros['cddepartamento'], texto: filtros['texto'] }, size, cursor, direction),
    idItemCatalogo: (item) => item.nmconsecutivo,
    construirBodyAsociar: (seleccionados) => ({
      zonas: seleccionados.map((z) => ({
        cddepartamento: z.cddepartamento,
        nmconsecutivoZona: z.nmconsecutivoZona,
        cdmunicipio: z.cdmunicipio,
        dslocalidad: z.dslocalidad,
        dsbarrio: z.dsbarrio
      }))
    }),
    construirBodyModificar: (fila, cambios) => ({
      cddepartamento: cambios['cddepartamento'] ?? fila.cddepartamento,
      nmconsecutivoZona: cambios['nmconsecutivoZona'] ?? fila.nmconsecutivoZona,
      cdmunicipio: cambios['cdmunicipio'] ?? fila.cdmunicipio,
      dslocalidad: cambios['dslocalidad'] ?? fila.dslocalidad,
      dsbarrio: cambios['dsbarrio'] ?? fila.dsbarrio
    }),
    camposEditables: [
      { campo: 'cddepartamento', label: 'Departamento (código)', tipo: 'texto' },
      { campo: 'cdmunicipio', label: 'Municipio (código)', tipo: 'texto' },
      { campo: 'dslocalidad', label: 'Localidad', tipo: 'texto' },
      { campo: 'dsbarrio', label: 'Barrio', tipo: 'texto' }
    ],
    // Panel de filtros en cascada del legado (hallazgo M-5): las opciones de
    // cada nivel salen de las zonas que el empleado ya tiene asignadas.
    filtrosCascada: [
      { campo: 'cddepartamento', label: 'Departamento', recurso: 'filtros/departamentos' },
      {
        campo: 'nmconsecutivoZona', label: 'Zona', recurso: 'filtros/zonas',
        paramsDesde: ['cddepartamento'], requiere: ['cddepartamento']
      },
      {
        campo: 'cdmunicipio', label: 'Municipio', recurso: 'filtros/municipios',
        paramsDesde: ['nmconsecutivoZona'], requiere: ['nmconsecutivoZona']
      },
      {
        // El municipio es opcional aquí porque CDMUNICIPIO es nullable en
        // TPSC_EMPLEADOS_ZONAS (hallazgo C-4).
        campo: 'dslocalidad', label: 'Localidad', recurso: 'filtros/localidades',
        paramsDesde: ['nmconsecutivoZona', 'cdmunicipio'], requiere: ['nmconsecutivoZona']
      },
      {
        campo: 'dsbarrio', label: 'Barrio', recurso: 'filtros/barrios',
        paramsDesde: ['dslocalidad'], requiere: ['dslocalidad']
      }
    ]
  };
}
